'use client';

import React from 'react';
import { useLanguage } from '@/context/LanguageContext';
import { usePortfolioData } from '@/context/PortfolioDataContext';
import AppImage from '@/components/ui/AppImage';

export default function ContactSection() {
  const { t } = useLanguage();
  const { hero, contact, siteSettings } = usePortfolioData();

  const name = hero?.name ?? siteSettings?.site_name ?? '';
  const title = hero?.title ?? '';
  const email = contact?.email ?? '';
  const linkedinUrl = contact?.linkedin_url ?? hero?.linkedin_url ?? '';
  const websiteUrl = contact?.website_url ?? '';
  const cvUrl = contact?.cv_url ?? hero?.cv_url ?? '';
  const logoUrl = siteSettings?.logo_url ?? '';
  const initials = siteSettings?.footer_initials ?? '';
  const websiteHandle = websiteUrl ? websiteUrl.replace(/^https?:\/\//, '').replace(/\/$/, '') : '';

  const socialLinks = [
    ...(linkedinUrl
      ? [
          {
            name: 'LinkedIn',
            handle: name,
            href: linkedinUrl,
            icon: (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <rect
                  x="2"
                  y="2"
                  width="16"
                  height="16"
                  rx="3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path
                  d="M6 8.5v5M6 6v.5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
                <path
                  d="M10 13.5v-3a2 2 0 0 1 4 0v3M10 8.5v5"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ),
            color: '#63b3ed',
            bg: 'rgba(99,179,237,0.08)',
            border: 'rgba(99,179,237,0.2)',
          },
        ]
      : []),
    ...(websiteUrl
      ? [
          {
            name: 'Website',
            handle: websiteHandle,
            href: websiteUrl,
            icon: (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <circle cx="10" cy="10" r="8" stroke="currentColor" strokeWidth="1.5" />
                <path
                  d="M10 2c0 0-3 3-3 8s3 8 3 8M10 2c0 0 3 3 3 8s-3 8-3 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path d="M2 10h16" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              </svg>
            ),
            color: '#a78bfa',
            bg: 'rgba(167,139,250,0.08)',
            border: 'rgba(167,139,250,0.2)',
          },
        ]
      : []),
    ...(cvUrl
      ? [
          {
            name: `${t?.nav_cv} / Özgeçmiş`,
            handle: t?.contact_cv_handle,
            href: cvUrl,
            icon: (
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path
                  d="M5 3h8l4 4v10a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"
                  stroke="currentColor"
                  strokeWidth="1.5"
                />
                <path d="M13 3v4h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
                <path
                  d="M10 9v6M7 12l3 3 3-3"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ),
            color: '#EDF2F7',
            bg: 'rgba(237,242,247,0.06)',
            border: 'rgba(237,242,247,0.12)',
          },
        ]
      : []),
  ];

  return (
    <section id="contact" className="relative py-28 px-6 overflow-hidden">
      <div
        className="absolute inset-0 z-0"
        style={{ background: 'linear-gradient(180deg, #141c28 0%, #1B2432 100%)' }}
      />
      <div
        className="absolute inset-0 pointer-events-none z-0"
        style={{
          background:
            'radial-gradient(ellipse 50% 60% at 50% 50%, rgba(99,179,237,0.08) 0%, transparent 70%)',
        }}
      />
      <div className="relative z-10 max-w-4xl mx-auto">
        {/* Section label */}
        <div className="reveal flex items-center gap-3 mb-4">
          <div
            className="h-px flex-1 max-w-[48px]"
            style={{ background: 'rgba(99,179,237,0.5)' }}
          />
          <span
            className="text-xs font-semibold uppercase tracking-[0.22em]"
            style={{ color: '#63b3ed' }}
          >
            {t?.contact_label}
          </span>
        </div>

        <div className="reveal reveal-delay-1 mb-14">
          <h2
            className="font-display font-bold mb-5"
            style={{
              fontSize: 'clamp(2rem, 5vw, 3.25rem)',
              color: '#EDF2F7',
              lineHeight: 1.1,
              letterSpacing: '-0.02em',
            }}
          >
            {t?.contact_heading_1}{' '}
            <span
              style={{
                background: 'linear-gradient(135deg, #63b3ed, #a78bfa)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
              }}
            >
              {t?.contact_heading_2}
            </span>
          </h2>
          <p
            className="text-lg max-w-xl leading-relaxed"
            style={{ color: 'rgba(237,242,247,0.55)' }}
          >
            {t?.contact_subtitle}
          </p>
        </div>

        {/* Main contact card */}
        <div
          className="reveal reveal-delay-2 glow-card rounded-3xl p-8 md:p-12 mb-8"
          style={{ background: 'rgba(255,255,255,0.03)' }}
        >
          <div className="flex flex-col md:flex-row gap-10 items-start">
            {/* Left: initials + name */}
            <div className="flex-shrink-0 flex flex-col items-center md:items-start gap-4">
              <div
                className="w-20 h-20 rounded-2xl flex items-center justify-center font-display font-bold text-2xl overflow-hidden"
                style={{
                  background:
                    'linear-gradient(135deg, rgba(99,179,237,0.2), rgba(167,139,250,0.2))',
                  border: '1px solid rgba(99,179,237,0.3)',
                  color: '#EDF2F7',
                }}
              >
                {logoUrl ? (
                  <AppImage
                    src={logoUrl}
                    alt={initials}
                    width={80}
                    height={80}
                    className="w-full h-full object-contain"
                  />
                ) : (
                  initials
                )}
              </div>
              <div>
                <p className="font-display font-bold text-lg" style={{ color: '#EDF2F7' }}>
                  {name}
                </p>
                <p className="text-sm" style={{ color: 'rgba(237,242,247,0.45)' }}>
                  {title}
                </p>
              </div>
              <div
                className="flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold"
                style={{
                  background: 'rgba(34,197,94,0.1)',
                  border: '1px solid rgba(34,197,94,0.3)',
                  color: '#22c55e',
                }}
              >
                <span className="w-1.5 h-1.5 rounded-full bg-green-400" />
                {t?.contact_available}
              </div>
            </div>

            {/* Right: contact options */}
            <div className="flex-1">
              <p
                className="text-base leading-relaxed mb-8"
                style={{ color: 'rgba(237,242,247,0.6)' }}
              >
                {t?.contact_quote}
              </p>

              {/* Email CTA */}
              {email ? (
                <a
                  href={`mailto:${email}`}
                  className="cta-pill inline-flex items-center gap-3 px-7 py-4 rounded-2xl font-display font-bold text-white text-base mb-6"
                >
                  <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                    <rect
                      x="2"
                      y="4"
                      width="16"
                      height="12"
                      rx="2"
                      stroke="white"
                      strokeWidth="1.5"
                    />
                    <path d="M2 7l8 5 8-5" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
                  </svg>
                  {email}
                </a>
              ) : null}

              {/* Location note */}
              <p className="text-sm" style={{ color: 'rgba(237,242,247,0.35)' }}>
                {t?.contact_response}
              </p>
            </div>
          </div>
        </div>

        {/* Social links */}
        <div className="reveal reveal-delay-3 flex flex-wrap gap-3">
          {socialLinks?.map((social) => (
            <a
              key={social?.name}
              href={social?.href}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 px-5 py-3 rounded-xl text-sm font-semibold transition-all duration-300 hover:scale-105"
              style={{
                background: social?.bg,
                border: `1px solid ${social?.border}`,
                color: social?.color,
              }}
            >
              {social?.icon}
              <span>{social?.name}</span>
              <span className="text-xs font-normal" style={{ color: 'rgba(237,242,247,0.35)' }}>
                {social?.handle}
              </span>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
